import { SpatialAudioOff, SpatialAudioOffOutlined } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { AxiosProgressEvent } from "axios";
import dayjs from "dayjs";
import { useRef, useState } from "react";

import emitSnackbar from "../../../../emitSnackbar";
import DragDrop from "../../Layout/DragDrop";
import DragDropParent from "../../Layout/DragDropParent";
import { Study } from "../../models";
import useStudyFileStore from "../../useStudyFileStore";
import { FileEditState } from "../manageStudyModels";
import { EditStudyContext } from "../useEditStudyContext";
import AudioFileStatusIndicator from "./AudioFileStatusIndicator";
import { AudioIconWrapper } from "./AudioIconWrapper";
import AudioMenu from "./AudioMenu";
import FileProgressSpinner from "./FileProgressSpinner";

const audioFileExtensions = ["mp3", "wav", "m4a", "flac", "aac", "wma", "ogg"];

type EditAudioFileUploadProps = {
  study: Study;
  edit_context: EditStudyContext;
  fileEditState: FileEditState;
};

export default function EditAudioFileUpload({ study, edit_context, fileEditState }: EditAudioFileUploadProps) {
  const StudyFileStore = useStudyFileStore();

  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const { file, uploaded_file_guid, audio_file_id, s3_file_location, original_name } = fileEditState;
  const fileUploadSaved = !!s3_file_location;
  const hasFile = !!(file || s3_file_location);

  const setUploadProgress = (progress?: number) => edit_context.setUploadProgress(fileEditState!, progress);

  const handleClickBrowse = () => {
    inputRef.current?.click();
  };

  const uploadFileWithProgress = async (file: File, study: Study, audio_file_id: string) => {
    setUploadProgress(-1);

    const original_file_name = file.name;
    const file_name = makeAudioFileName(file, study, audio_file_id);

    const register = await StudyFileStore.registerFile(file_name, original_file_name);
    console.debug("File Registration UUID:", register.deepsense_file_guid);

    const s3_upload = await StudyFileStore.generateUploadUrl(register.deepsense_file_guid);
    console.debug("S3 Presigned URL:", s3_upload.presigned_url);

    // Set upload progress to value to make control appear
    setUploadProgress(1);
    await StudyFileStore.uploadFile(s3_upload.presigned_url, file, (progressEvent: AxiosProgressEvent) => {
      const { loaded, total, rate } = progressEvent;

      if (loaded < (total ?? 1)) {
        const progress = (loaded * 100) / (total ?? 1);
        console.debug({ total, loaded, progress, rate });
        setUploadProgress(progress);
      } else {
        // Force last update frame to 100% since we don't always get a final frame
        setUploadProgress(100);

        setTimeout(() => {
          // Transition to a "working" animation
          setUploadProgress(-1);
          // Then then hide the spinner after 1 second
          setTimeout(() => setUploadProgress(undefined), 1000);
        }, 200);
      }
    });

    return register.deepsense_file_guid;
  };

  const handleFileSelect = async (file?: File) => {
    if (!file) {
      return;
    }

    if (edit_context.uploadFileExists(file)) {
      return emitSnackbar(
        `This file ${file?.name} has been uploaded more than once. Please review uploaded files and make the necessary changes.`,
        "info",
        { autoHideDuration: 10000 },
      );
    }

    // File extension check
    const extension = file.name.split(".").pop()?.toLowerCase();
    if (!audioFileExtensions.includes(extension!)) {
      const allowedExtensionsText = audioFileExtensions.map((ext: string) => `.${ext}`).join(", ");

      return emitSnackbar(`Only ${allowedExtensionsText} files are allowed`, "error", {
        autoHideDuration: 3000,
      });
    }

    if (!audio_file_id) {
      // Shouldn't get to this state unless there is an issue with the Interview that was passed in
      return emitSnackbar(`Interview is missing an audio_file_id, please contact support`, "warning", {
        autoHideDuration: 3000,
      });
    }

    edit_context.setAudioFile(audio_file_id, file);

    const deepsense_file_guid = await uploadFileWithProgress(file, study, audio_file_id).catch((reason) => {
      console.error("Filed to upload file", reason);
      // Unset the file on error
      edit_context.setAudioFile(audio_file_id, undefined);
      setUploadProgress(undefined);

      emitSnackbar(`There was an error while uploading the selected file`, "warning", {
        persist: true,
      });
    });

    if (deepsense_file_guid) {
      edit_context.setAudioFile(audio_file_id, file, deepsense_file_guid);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length) {
      const file = droppedFiles[0];
      handleFileSelect(file);
    }
  };

  const handleClose = async (type: "delete" | "replace" | "download" | "rerun") => {
    if (type === "delete") {
      if (audio_file_id && uploaded_file_guid) {
        // Unset the File and GUID reference to remove it from the context
        edit_context.setAudioFile(audio_file_id, undefined, undefined);
      }
    }

    if (type === "download") {
      const file_name = (s3_file_location ?? "").split("/").at(-1);

      if (uploaded_file_guid && file_name) {
        StudyFileStore.downloadFile(uploaded_file_guid, file_name);
      }
    }
  };

  const makeAudioFileName = (file: File, study: Study, audio_file_id: string) => {
    const file_extension = file.name.split(".").pop();

    const newFileName =
      edit_context.sanitizeString(
        [
          study.vendor.vendor_name,
          study.study_name,
          "audio_file",
          audio_file_id,
          dayjs(study.study_start_date).format("YYYYMMDD") ?? "",
        ].join("_"),
      ) + `.${file_extension}`;

    return newFileName;
  };

  return (
    <DragDropParent hasFile={hasFile} isDragging={isDragging}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <Box
          sx={{
            width: "3rem",
            height: "3rem",
            background: hasFile ? " #00A3E0" : " rgba(0, 163, 224, 0.3)",
            display: "flex",
            borderRadius: "5px",
            justifyContent: "center",
            alignItems: "center",
            flexShrink: "0",
          }}
        >
          <AudioIconWrapper IconComponent={hasFile ? SpatialAudioOff : SpatialAudioOffOutlined} />
        </Box>
        {audio_file_id && (
          <Box sx={{ minWidth: "150px" }}>
            <Typography variant="h6" sx={{ fontSize: "16px", fontWeight: "400", color: "#13294B" }}>
              Audio ID: {audio_file_id}
            </Typography>
          </Box>
        )}
      </Box>
      <Box sx={{ flex: 1 }}>
        {hasFile ? (
          <Box sx={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", flexDirection: "column", marginLeft: "10px" }}>
              <Typography variant="h6" sx={{ fontSize: "16px", fontWeight: "400", color: "13294B" }}>
                {s3_file_location
                  ? (s3_file_location ?? "").split("/").at(-1)
                  : file && audio_file_id && makeAudioFileName(file, study, audio_file_id)}
              </Typography>
              {(original_name || file?.name) && (
                // TODO: This has a wiggle to it, need to revise styling on this and the one below
                <Typography variant="h6" sx={{ fontSize: "12px", fontWeight: "400", color: "13294B" }}>
                  Original file name: {original_name ?? file?.name}
                </Typography>
              )}
            </Box>

            {hasFile && ( // interview.audio_file.uploaded_file
              <Box sx={{ display: "flex", gap: "1rem", alignItems: "center", justifyContent: "center" }}>
                {fileUploadSaved && <AudioFileStatusIndicator status={fileEditState.process_status} />}

                {/* <CustomButtonGroup label="TEXT" Icon={DocumentScannerOutlined} handleOnClick={() => {}} /> */}

                <AudioMenu onSelect={handleClose} />
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ display: "flex" }}>
            <DragDrop
              acceptedFiles={audioFileExtensions.map((_) => `.${_}`).join(",")} // ex: ".mp3,.wav,.flac,.m4a,.aac,.wma,.ogg,.oga,.webm,.amr,.3gp"
              inputRef={inputRef}
              onFileSelect={handleFileSelect}
              onClickBrowse={handleClickBrowse}
              handleDrop={handleDrop}
              setIsDragging={setIsDragging}
            />
            <Box style={{ width: "30px" }}></Box>
          </Box>
        )}
      </Box>
      <FileProgressSpinner progress={fileEditState.uploadProgress} />
    </DragDropParent>
  );
}
